import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Icon } from '../ui/shell';
import { themeVars, flowComponents } from '../flows/flowKit';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ============================================================
   CourseWizard — "Create Course" guided/auto overlay, ported from
   the reference coursewizard.jsx and extended with an editable
   Domains & subdomains step (the project's course model, which the
   reference omitted). This is the create path; editing an existing
   course still uses the legacy course/dialog.js.

   Course model produced: { name, category, level, description,
     mode, domains: [{ name, percentage, domainId,
       subDomain: [{ name, subTopics }] }] }
   API wiring is intentionally deferred (onComplete + draft stash).
   ============================================================ */

function cwSeq(mode) {
  const base = [
    { key: 'create', label: 'Course info', sub: 'Name & details' },
    { key: 'upload', label: 'Upload material', sub: 'Docs & video' },
    { key: 'mode', label: 'Build method', sub: 'Auto or guided' },
    { key: 'domains', label: 'Domains & subdomains', sub: 'Weighting & topics' },
  ];
  if (mode === 'auto') return [...base,
    { key: 'confirm-template', label: 'Assessment template', sub: 'Settings to apply' },
    { key: 'build', label: 'Yaki builds', sub: 'Course & assessments' },
    { key: 'review', label: 'Review course', sub: 'Confirm or edit' },
    { key: 'baseline', label: 'Baseline assessment', sub: 'Starting point' },
    { key: 'settings', label: 'Settings', sub: 'Publish options' }];
  if (mode === 'guided') return [...base,
    { key: 'outline', label: 'Build outline', sub: 'Modules & lessons' },
    { key: 'assessments', label: 'Add assessments', sub: 'Per module' },
    { key: 'review', label: 'Review course', sub: 'Confirm or edit' },
    { key: 'baseline', label: 'Baseline assessment', sub: 'Starting point' },
    { key: 'settings', label: 'Settings', sub: 'Publish options' }];
  return base.slice(0, 3); // before a mode is chosen, only show create/upload/mode
}

const generateDomainId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 10; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
  return id;
};

const Wrap = styled.div`
  ${themeVars}
  ${flowComponents}

  &.cw { position: fixed; inset: 0; z-index: 60; background: var(--bg); display: grid; grid-template-columns: 280px 1fr; color: var(--text); font-family: var(--font-body); }
  .cw__rail { background: var(--surface-2); border-right: 1px solid var(--border); padding: 26px 22px; display: flex; flex-direction: column; }
  .cw__brand { display: flex; align-items: center; gap: 10px; margin-bottom: 28px; }
  .cw__brand .rail__logo { width: 34px; height: 34px; font-size: 16px; }
  .cw__brand b { font-family: var(--font-head); font-weight: 750; font-size: 15px; }
  .cw__steps { display: flex; flex-direction: column; gap: 2px; }
  .cw__step { display: flex; gap: 12px; align-items: flex-start; padding: 9px 10px; border-radius: 10px; font-size: 13.5px; font-weight: 600; color: var(--text-muted); position: relative; }
  .cw__step.is-now { background: var(--accent-soft); color: var(--accent-strong); }
  .cw__step.is-done { color: var(--text); }
  .cw__step-num { width: 22px; height: 22px; border-radius: 50%; flex: none; display: grid; place-items: center; font-size: 11px; font-weight: 700; background: var(--surface-3); color: var(--text-muted); }
  .cw__step.is-now .cw__step-num { background: var(--accent); color: #fff; }
  .cw__step.is-done .cw__step-num { background: var(--success); color: #fff; }
  .cw__step small { display: block; font-weight: 500; color: var(--text-subtle); font-size: 11.5px; margin-top: 1px; }
  .cw__railfoot { margin-top: auto; font-size: 12px; color: var(--text-subtle); }
  .cw__main { display: flex; flex-direction: column; overflow: hidden; }
  .cw__top { display: flex; align-items: center; gap: 12px; padding: 18px 30px; border-bottom: 1px solid var(--border); }
  .cw__top h2 { font-family: var(--font-head); font-size: 18px; font-weight: 720; margin: 0; }
  .cw__top .cw__crumb { font-size: 12px; color: var(--text-subtle); font-weight: 600; }
  .cw__body { flex: 1; overflow-y: auto; padding: 32px clamp(24px, 5vw, 70px); }
  .cw__bodyinner { max-width: 720px; margin: 0 auto; }
  .cw__foot { display: flex; align-items: center; gap: 12px; padding: 16px 30px; border-top: 1px solid var(--border); background: var(--surface); }
  .cw__foot .btn { height: 44px; }
  .x-btn { margin-left: auto; }

  .cw__h { font-family: var(--font-head); font-size: 23px; font-weight: 740; letter-spacing: -.01em; margin: 0 0 6px; }
  .cw__lead { margin: 0 0 24px; font-size: 14.5px; color: var(--text-muted); line-height: 1.55; }

  .cw__drops { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .drop { border: 1.5px dashed var(--border-strong); border-radius: var(--r-lg); background: var(--surface-2); padding: 22px 18px; text-align: center; cursor: pointer; transition: .16s; }
  .drop:hover { border-color: var(--accent); background: var(--accent-soft); }
  .drop__ico { width: 42px; height: 42px; border-radius: 12px; margin: 0 auto 10px; display: grid; place-items: center; background: var(--surface); color: var(--accent-strong); box-shadow: var(--shadow-sm); }
  .drop h4 { margin: 0 0 3px; font-size: 14px; font-weight: 700; }
  .drop p { margin: 0; font-size: 12px; color: var(--text-muted); }

  .cw__filelist { display: grid; gap: 8px; margin-top: 16px; }
  .cw__file { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--surface); font-size: 13.5px; }
  .cw__file .cw__file-ico { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; background: var(--surface-3); color: var(--text-muted); flex: none; }
  .cw__file b { font-weight: 650; }
  .cw__file span { color: var(--text-subtle); font-size: 12px; }

  .cw__build { display: grid; gap: 14px; max-width: 520px; margin: 8px auto; }
  .cw__buildrow { display: flex; align-items: center; gap: 13px; padding: 14px 16px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--surface); font-size: 14px; font-weight: 600; transition: .25s; }
  .cw__buildrow.is-pending { opacity: .5; }
  .cw__buildrow.is-active { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
  .cw__buildrow .cw__buildico { width: 30px; height: 30px; border-radius: 50%; flex: none; display: grid; place-items: center; background: var(--surface-3); color: var(--text-muted); }
  .cw__buildrow.is-done .cw__buildico { background: var(--success); color: #fff; }
  .cw__buildrow.is-active .cw__buildico { background: var(--accent); color: #fff; }
  .cw__spin { width: 16px; height: 16px; border-radius: 50%; border: 2.5px solid rgba(255,255,255,.4); border-top-color: #fff; animation: ob-spin .7s linear infinite; }
  @keyframes ob-spin { to { transform: rotate(360deg); } }

  .cw__module { border: 1px solid var(--border); border-radius: var(--r-md); background: var(--surface); margin-bottom: 10px; overflow: hidden; }
  .cw__module-h { display: flex; align-items: center; gap: 11px; padding: 13px 16px; font-weight: 700; font-size: 14px; }
  .cw__module-h .num { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; font-size: 12px; background: var(--accent-soft); color: var(--accent-strong); font-weight: 700; flex: none; }
  .cw__module-b { padding: 0 16px 13px 51px; display: grid; gap: 6px; }
  .cw__lesson { display: flex; align-items: center; gap: 9px; font-size: 13px; color: var(--text-muted); }
  .cw__lesson svg { color: var(--text-subtle); flex: none; }

  .cw__success { text-align: center; max-width: 460px; margin: 6vh auto; }
  .cw__success-ico { width: 76px; height: 76px; border-radius: 50%; margin: 0 auto 20px; display: grid; place-items: center; background: color-mix(in oklch, var(--success) 16%, var(--surface)); color: var(--success); }

  .cw__radios { display: grid; gap: 12px; }
  .cw__radios--2 { grid-template-columns: 1fr 1fr; }
  .choice { display: flex; gap: 14px; align-items: flex-start; text-align: left; padding: 17px 18px; border-radius: var(--r-lg); border: 1.5px solid var(--border); background: var(--surface); cursor: pointer; transition: .16s; width: 100%; }
  .choice:hover { border-color: var(--accent); background: var(--accent-soft); }
  .choice.is-sel { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
  .choice__ico { width: 42px; height: 42px; border-radius: 12px; flex: none; display: grid; place-items: center; background: var(--accent-soft); color: var(--accent-strong); box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--accent) 22%, transparent); }
  .choice__txt h4 { margin: 0 0 3px; font-size: 15px; font-weight: 700; }
  .choice__txt p { margin: 0; font-size: 13px; color: var(--text-muted); line-height: 1.5; }

  /* domains editor */
  .dom__bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 13px; font-weight: 650; }
  .dom__bar small { font-weight: 600; color: var(--text-subtle); }
  .dom__bar .over { color: var(--danger); }
  .dom__add { display: grid; grid-template-columns: 1fr 120px auto; gap: 10px; margin-bottom: 16px; }
  .dom__sub-add { display: grid; grid-template-columns: 1fr 1fr auto; gap: 10px; margin: 10px 0 2px; }
  .dom__pct { font-size: 12px; font-weight: 700; color: var(--accent-strong); background: var(--accent-soft); border-radius: var(--r-pill); padding: 3px 10px; }
  .dom__chiprow { display: flex; flex-wrap: wrap; gap: 6px; }
  .dom__chip { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; padding: 4px 10px; border-radius: var(--r-pill); background: var(--surface-3); color: var(--text-muted); }
  .dom__chip button { border: none; background: none; cursor: pointer; color: var(--text-subtle); display: grid; place-items: center; padding: 0; }
  .dom__empty { color: var(--text-subtle); font-size: 13px; padding: 6px 0; }

  @media (max-width: 920px) {
    &.cw { grid-template-columns: 1fr; }
    .cw__rail { display: none; }
    .cw__drops, .cw__radios--2 { grid-template-columns: 1fr; }
    .dom__add, .dom__sub-add { grid-template-columns: 1fr; }
  }
`;

/* -------- Domains & subdomains editor (project model) -------- */
function DomainsStep({ domains, setDomains, guardDomains }) {
  const [name, setName] = useState('');
  const [pct, setPct] = useState('');
  const [active, setActive] = useState(null);
  const [subName, setSubName] = useState('');
  const [subTopics, setSubTopics] = useState('');
  const [err, setErr] = useState('');

  const total = domains.reduce((s, d) => s + parseFloat(d.percentage || 0), 0);

  const addDomain = () => {
    if (!guardDomains()) return;
    const p = parseFloat(pct);
    if (!name.trim() || isNaN(p) || p <= 0 || p > 100) { setErr('Enter a domain name and a percentage between 1 and 100.'); return; }
    if (total + p > 100) { setErr('Total domain percentage cannot exceed 100%.'); return; }
    setDomains([...domains, { name: name.trim(), percentage: String(p), domainId: generateDomainId(), subDomain: [] }]);
    setName(''); setPct(''); setErr('');
  };

  const removeDomain = (i) => {
    if (!guardDomains()) return;
    setDomains(domains.filter((_, j) => j !== i));
  };

  const addSub = (i) => {
    if (!guardDomains()) return;
    if (!subName.trim() || !subTopics.trim()) { setErr('Fill both subdomain fields.'); return; }
    setDomains(domains.map((d, j) => j === i ? { ...d, subDomain: [...d.subDomain, { name: subName.trim(), subTopics: subTopics.trim() }] } : d));
    setSubName(''); setSubTopics(''); setActive(null); setErr('');
  };

  const removeSub = (i, k) => {
    if (!guardDomains()) return;
    setDomains(domains.map((d, j) => j === i ? { ...d, subDomain: d.subDomain.filter((_, m) => m !== k) } : d));
  };

  return (
    <>
      <h2 className="cw__h">Domains & subdomains</h2>
      <p className="cw__lead">Define the domains this course covers and how each is weighted. Add subdomains to break a domain into focused topics.</p>

      <div className="dom__bar">
        <span>Weighting</span>
        <small className={total > 100 ? 'over' : ''}>{total}% of 100% allocated · {Math.max(0, 100 - total)}% remaining</small>
      </div>

      <div className="dom__add">
        <input className="input" placeholder="Domain name (e.g. Pharmacokinetics)" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="input" type="number" min="1" max="100" placeholder="Weight %" value={pct} onChange={(e) => setPct(e.target.value)} />
        <button className="btn btn--primary" onClick={addDomain}><Icon name="plus" size={15} />Add</button>
      </div>
      {err && <div className="actas" style={{ marginBottom: 14, color: 'var(--danger)' }}>{err}</div>}

      {domains.length === 0 && <div className="dom__empty">No domains yet — add your first above.</div>}

      {domains.map((d, i) => (
        <div className="cw__module" key={d.domainId || i}>
          <div className="cw__module-h">
            <span className="num">{i + 1}</span>{d.name}
            <span className="dom__pct" style={{ marginLeft: 'auto' }}>{d.percentage}%</span>
            <button className="btn btn--sm btn--ghost" style={{ marginLeft: 8 }} onClick={() => removeDomain(i)}><Icon name="trash" size={14} /></button>
          </div>
          <div className="cw__module-b">
            {d.subDomain.length > 0 && (
              <div className="dom__chiprow">
                {d.subDomain.map((s, k) => (
                  <span className="dom__chip" key={k} title={s.subTopics}>
                    {s.name}
                    <button onClick={() => removeSub(i, k)} aria-label="Remove subdomain"><Icon name="x" size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            {active === i ? (
              <div className="dom__sub-add">
                <input className="input" placeholder="Subdomain name" value={subName} onChange={(e) => setSubName(e.target.value)} />
                <input className="input" placeholder="Sub-topics (comma separated)" value={subTopics} onChange={(e) => setSubTopics(e.target.value)} />
                <button className="btn btn--subtle" onClick={() => addSub(i)}>Save</button>
              </div>
            ) : (
              <div className="cw__lesson" style={{ color: 'var(--accent-strong)', fontWeight: 650, cursor: 'pointer' }} onClick={() => {
                if (!guardDomains()) return;
                setActive(i); setErr('');
              }}>
                <Icon name="plus" size={13} />Add subdomain
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

export default function CourseWizard({ onClose, onComplete }) {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const guardDomains = () => guardAction('action.course.wizard.domains');
  const [mode, setMode] = useState(null);
  const [step, setStep] = useState('create');
  const [name, setName] = useState('Clinical Pharmacology II');
  const [domains, setDomains] = useState([]);
  const [files, setFiles] = useState([
    { name: 'Pharmacology — Module 1.pdf', meta: 'PDF · 2.1 MB', ic: 'doc' },
    { name: 'Lecture recording.mp4', meta: 'Video · 84 MB', ic: 'play' },
  ]);

  const seq = cwSeq(mode);
  const idx = seq.findIndex((s) => s.key === step);
  const next = () => {
    if (step === 'upload' && !guardAction('action.course.wizard.upload')) return;
    const n = seq[idx + 1]; if (n) setStep(n.key); else setStep('done');
  };
  const back = () => { const p = seq[idx - 1]; if (p) setStep(p.key); };

  const [buildN, setBuildN] = useState(0);
  const buildRows = ['Reading your material', 'Mapping topics & subtopics', 'Drafting 6 lessons', 'Generating 42 questions', 'Assembling assessments'];
  useEffect(() => {
    if (step !== 'build') { setBuildN(0); return; }
    const t = setInterval(() => setBuildN((n) => { if (n >= buildRows.length) { clearInterval(t); return n; } return n + 1; }), 850);
    return () => clearInterval(t);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  const buildDone = buildN >= buildRows.length;

  const finish = () => {
    if (!guardAction('action.course.wizard.publish')) return;
    const draft = { name, mode, domains };
    try { sessionStorage.setItem('newCourseDraft', JSON.stringify(draft)); } catch (_) { /* non-fatal */ }
    onComplete && onComplete(name, draft);
  };

  const body = () => {
    switch (step) {
      case 'create': return (
        <>
          <h2 className="cw__h">Create a new course</h2>
          <p className="cw__lead">Give Yaki the basics. You'll add your material on the next step.</p>
          <div className="field"><label>Course name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-grid">
            <div className="field"><label>Category</label><select className="select"><option>Clinical</option><option>Compliance</option><option>Soft skills</option><option>Administrative</option></select></div>
            <div className="field"><label>Level</label><select className="select" defaultValue="Advanced"><option>Beginner</option><option>Intermediate</option><option>Advanced</option></select></div>
          </div>
          <div className="field"><label>Short description</label><textarea className="textarea" defaultValue="Advanced pharmacokinetics, drug interactions and clinical decision-making for senior cohorts." /></div>
        </>
      );
      case 'upload': return (
        <>
          <h2 className="cw__h">Upload your course material</h2>
          <p className="cw__lead">Add anything you teach from — Yaki reads it all to draft your course and questions.</p>
          <div className="cw__drops">
            {[['doc', 'Documents', 'PDF, DOCX, PPTX'], ['upload', 'Files', 'Drag & drop'], ['play', 'Video', 'MP4 or a link'], ['globe', 'Web URL', 'Paste a source']].map(([ic, h, p]) => (
              <div className="drop" key={h}><div className="drop__ico"><Icon name={ic} size={20} /></div><h4>{h}</h4><p>{p}</p></div>
            ))}
          </div>
          <div className="cw__filelist">
            {files.map((f, i) => (
              <div className="cw__file" key={i}>
                <div className="cw__file-ico"><Icon name={f.ic} size={16} /></div>
                <div style={{ flex: 1 }}><b>{f.name}</b><br /><span>{f.meta}</span></div>
                <button className="btn btn--sm btn--ghost" onClick={() => setFiles(files.filter((_, j) => j !== i))}><Icon name="x" size={16} /></button>
              </div>
            ))}
          </div>
        </>
      );
      case 'mode': return (
        <>
          <h2 className="cw__h">How should Yaki build this course?</h2>
          <p className="cw__lead">Let Yaki do it end-to-end, or stay in control and build it step by step.</p>
          <div className="cw__radios cw__radios--2">
            <button className={'choice' + (mode === 'auto' ? ' is-sel' : '')} onClick={() => setMode('auto')}>
              <div className="choice__ico"><Icon name="sparkles" size={20} /></div>
              <div className="choice__txt"><h4>Auto — let Yaki build it</h4><p>Yaki drafts the full course and assessments from your material. You review before publishing.</p></div>
            </button>
            <button className={'choice' + (mode === 'guided' ? ' is-sel' : '')} onClick={() => setMode('guided')}>
              <div className="choice__ico"><Icon name="layers" size={20} /></div>
              <div className="choice__txt"><h4>Guided — build it together</h4><p>You shape the outline and assessments; Yaki suggests as you go. Best for tightly-scoped courses.</p></div>
            </button>
          </div>
        </>
      );
      case 'domains': return <DomainsStep domains={domains} setDomains={setDomains} guardDomains={guardDomains} />;
      case 'confirm-template': return (
        <>
          <h2 className="cw__h">Confirm the assessment template</h2>
          <p className="cw__lead">Yaki will apply these defaults to every assessment it drafts. You can fine-tune later.</p>
          <div className="form-grid">
            <div className="field"><label>Question type</label><select className="select" defaultValue="Mixed"><option>Mixed</option><option>Multiple choice</option><option>Short answer</option></select></div>
            <div className="field"><label>Questions per assessment</label><input className="input" defaultValue="20" /></div>
            <div className="field"><label>Difficulty</label><select className="select" defaultValue="Adaptive"><option>Adaptive</option><option>Easy</option><option>Medium</option><option>Hard</option></select></div>
            <div className="field"><label>Pass mark</label><input className="input" defaultValue="70%" /></div>
          </div>
          <div className="set-row" style={{ padding: '14px 0', borderTop: '1px solid var(--border)' }}>
            <div className="set-row__txt"><h4>Generate an answer rationale for each question</h4><p>Helps learners understand why an answer is right</p></div>
            <div className="switch on" />
          </div>
        </>
      );
      case 'build': return (
        <>
          <h2 className="cw__h">{buildDone ? 'Your course is ready to review' : 'Yaki is building your course…'}</h2>
          <p className="cw__lead">{buildDone ? '6 lessons and 3 assessments drafted from your material.' : 'This usually takes under a minute. Hang tight.'}</p>
          <div className="cw__build">
            {buildRows.map((r, i) => {
              const state = i < buildN ? 'is-done' : i === buildN ? 'is-active' : 'is-pending';
              return (
                <div className={'cw__buildrow ' + state} key={r}>
                  <span className="cw__buildico">{i < buildN ? <Icon name="check" size={15} /> : i === buildN ? <span className="cw__spin" /> : <Icon name="clock" size={14} />}</span>
                  {r}
                </div>
              );
            })}
          </div>
        </>
      );
      case 'outline': return (
        <>
          <h2 className="cw__h">Build your course outline</h2>
          <p className="cw__lead">Add the modules and lessons you want to teach. Yaki suggests lessons from your material as you type.</p>
          {(domains.length ? domains.map((d) => [`${d.name}`, d.subDomain.map((s) => s.name)]) : [['Module 1 · Pharmacokinetics', ['Absorption & distribution', 'Metabolism & first-pass effect', 'Clearance & half-life']], ['Module 2 · Drug interactions', ['CYP450 pathways', 'Managing polypharmacy']]]).map(([m, ls], i) => (
            <div className="cw__module" key={m}>
              <div className="cw__module-h"><span className="num">{i + 1}</span>{m}</div>
              <div className="cw__module-b">
                {ls.map((l) => <div className="cw__lesson" key={l}><Icon name="doc" size={13} />{l}</div>)}
                <div className="cw__lesson" style={{ color: 'var(--accent-strong)', fontWeight: 650, cursor: 'pointer' }}><Icon name="plus" size={13} />Add lesson</div>
              </div>
            </div>
          ))}
          <button className="btn btn--ghost btn--sm" style={{ marginTop: 4 }}><Icon name="plus" size={14} />Add module</button>
        </>
      );
      case 'assessments': return (
        <>
          <h2 className="cw__h">Add assessments</h2>
          <p className="cw__lead">Attach an assessment to each module. Yaki can draft the questions for you to review.</p>
          {['Pharmacokinetics quiz', 'Drug interactions check'].map((a, i) => (
            <div className="cw__file" key={a} style={{ marginBottom: 8 }}>
              <div className="cw__file-ico" style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}><Icon name="assess" size={16} /></div>
              <div style={{ flex: 1 }}><b>{a}</b><br /><span>Module {i + 1} · 15 questions · AI draft</span></div>
              <span className="tag tag--accent">Drafted</span>
            </div>
          ))}
          <button className="btn btn--ghost btn--sm" style={{ marginTop: 4 }}><Icon name="sparkles" size={14} />Draft another with Yaki</button>
        </>
      );
      case 'review': return (
        <>
          <h2 className="cw__h">Review your course</h2>
          <p className="cw__lead">Here's the full draft. Confirm to continue, or request changes and Yaki will revise it.</p>
          {(domains.length ? domains.map((d) => [d.name, d.subDomain.map((s) => s.name)]) : [['Pharmacokinetics', ['Absorption & distribution', 'Metabolism & first-pass effect']], ['Drug interactions', ['CYP450 pathways', 'Managing polypharmacy']]]).map((m, i) => (
            <div className="cw__module" key={m[0]}>
              <div className="cw__module-h"><span className="num">{i + 1}</span>{m[0]}<span className="tag tag--gray" style={{ marginLeft: 'auto' }}>{m[1].length} subdomains</span></div>
              <div className="cw__module-b">{m[1].map((l) => <div className="cw__lesson" key={l}><Icon name="doc" size={13} />{l}</div>)}</div>
            </div>
          ))}
          <div className="actas" style={{ margin: '6px 0 0' }}><Icon name="sparkles" size={16} /><span>Need changes? <b style={{ cursor: 'pointer' }}>Request updates</b> and Yaki will revise this draft.</span></div>
        </>
      );
      case 'baseline': return (
        <>
          <h2 className="cw__h">Develop a baseline assessment</h2>
          <p className="cw__lead">Yaki recommends a short baseline so you can see what learners already know before they start.</p>
          <div className="cw__file" style={{ marginBottom: 14 }}>
            <div className="cw__file-ico" style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}><Icon name="target" size={16} /></div>
            <div style={{ flex: 1 }}><b>Baseline — {name}</b><br /><span>12 questions · covers all domains · ~15 min</span></div>
            <span className="tag tag--accent">Recommended</span>
          </div>
          <div className="set-row" style={{ padding: '13px 0', borderTop: '1px solid var(--border)' }}>
            <div className="set-row__txt"><h4>Assign baseline automatically on enrolment</h4><p>Each learner takes it before lesson 1</p></div>
            <div className="switch on" />
          </div>
          <div className="set-row" style={{ padding: '13px 0', borderTop: '1px solid var(--border)' }}>
            <div className="set-row__txt"><h4>Use results to personalise each learner's path</h4><p>Yaki adapts difficulty from the baseline</p></div>
            <div className="switch on" />
          </div>
        </>
      );
      case 'settings': return (
        <>
          <h2 className="cw__h">Course & assessment settings</h2>
          <p className="cw__lead">Last step — set how this course behaves, then publish.</p>
          <div className="form-grid">
            <div className="field"><label>Visibility</label><select className="select" defaultValue="Organisation only"><option>Organisation only</option><option>Invite only</option><option>Public catalog</option></select></div>
            <div className="field"><label>Enrolment opens</label><input className="input" type="date" defaultValue="2026-06-20" /></div>
          </div>
          {[['Notify learners when published', true], ['Allow assessment retakes (up to 2)', true], ['Issue a certificate on completion', false]].map(([l, on]) => (
            <div className="set-row" key={l} style={{ padding: '13px 0', borderTop: '1px solid var(--border)' }}>
              <div className="set-row__txt"><h4>{l}</h4></div>
              <div className={'switch' + (on ? ' on' : '')} />
            </div>
          ))}
        </>
      );
      case 'done': return (
        <div className="cw__success">
          <div className="cw__success-ico"><Icon name="check" size={38} /></div>
          <h2 className="cw__h" style={{ fontSize: 26 }}>{name} is live</h2>
          <p className="cw__lead" style={{ fontSize: 15 }}>Yaki has told your learners to start exploring — they'll be notified the moment enrolment opens. You'll get a summary once the baseline results come in.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8 }}>
            <button className="btn btn--ghost" onClick={onClose}>Close</button>
            <button className="btn btn--primary" onClick={finish}><Icon name="course" size={16} />Go to course</button>
          </div>
        </div>
      );
      default: return null;
    }
  };

  const showFoot = step !== 'done';
  const nextLabel = step === 'mode' ? 'Continue' : step === 'settings' ? 'Publish course' : step === 'review' ? 'Confirm & continue' : step === 'build' ? (buildDone ? 'Review course' : 'Building…') : 'Continue';
  const nextDisabled = (step === 'mode' && !mode) || (step === 'build' && !buildDone) || (step === 'domains' && domains.length === 0);

  return (
    <>
    <Wrap className="cw">
      <aside className="cw__rail">
        <div className="cw__brand"><div className="rail__logo">S</div><b>New course</b></div>
        <div className="cw__steps">
          {seq.map((s, i) => (
            <div key={s.key} className={'cw__step' + (s.key === step ? ' is-now' : i < idx ? ' is-done' : '')}>
              <span className="cw__step-num">{i < idx ? <Icon name="check" size={12} /> : i + 1}</span>
              <div>{s.label}<small>{s.sub}</small></div>
            </div>
          ))}
        </div>
        <div className="cw__railfoot"><Icon name="sparkles" size={13} style={{ verticalAlign: '-2px' }} /> Yaki assists at every step</div>
      </aside>

      <div className="cw__main">
        <div className="cw__top">
          <div style={{ flex: 1 }}><div className="cw__crumb">Course creation</div><h2>{name || 'New course'}</h2></div>
          <button className="icon-btn x-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="cw__body"><div className="cw__bodyinner">{body()}</div></div>
        {showFoot && (
          <div className="cw__foot">
            {idx > 0 ? <button className="btn btn--ghost" onClick={back}><Icon name="chevR" size={16} style={{ transform: 'rotate(180deg)' }} />Back</button> : <button className="btn btn--ghost" onClick={onClose}>Cancel</button>}
            <div style={{ flex: 1 }} />
            <button className="btn btn--primary" onClick={next} disabled={nextDisabled} style={{ opacity: nextDisabled ? 0.55 : 1 }}>{nextLabel}{!nextDisabled && <Icon name="chevR" size={16} />}</button>
          </div>
        )}
      </div>
    </Wrap>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
